import { DISMISS, Form, Input, useNotification } from "collections";
import { ModalForm, useModalDispatch } from "collections/Modal";
import { CancelButton, SubmitButton } from "elements";
import {
  DeviceIn,
  useCreateDeviceGroupMutation,
  useDeviceGroupsQuery
} from "generated/graphql";
import { generatePath, Link } from "polyrouter";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import React from "react";
import { protect } from "TsRoleGuards";
import i18n, { useTranslation } from "utilities/i18n";
import { InferType, object, string, array } from "yup";

export const NewDeviceGroupSchema = object({
  name: string()
    .label(i18n.t("NAME"))
    .required()
    .test(
      "unique group name",
      i18n.t("DEVICE_GROUP_NAME_UNIQUE_ERROR"),
      function(value: string) {
        const { allGroups } = this.parent;
        return (
          !allGroups ||
          allGroups.every(
            (groupName: string) =>
              groupName.toLocaleLowerCase() !== value.toLocaleLowerCase()
          )
        );
      }
    )
    .ensure(),
  allGroups: array()
    .of(string())
    .default([])
});

export type DeviceGroupType = InferType<typeof NewDeviceGroupSchema>;

const CreateDeviceGroupModal: React.FC<{
  devices?: DeviceIn[];
  onSuccess?: () => void;
}> = ({ devices, onSuccess }) => {
  const { t } = useTranslation();
  const dismissModal = useModalDispatch();
  const [addNotification] = useNotification();
  const [, createDeviceGroup] = useCreateDeviceGroupMutation();
  const tenantId = useTenantId();

  const { data, fetching } = useDeviceGroupsQuery({
    variables: {
      tenantId
    }
  });
  const allGroups = data?.getAllGroups.groups?.groups;

  const initialValues = {
    name: "",
    allGroups: allGroups?.map(group => group.name) ?? []
  };

  const onSubmit = async (group: DeviceGroupType) => {
    const { data, error } = await createDeviceGroup({
      groupName: group.name,
      devices: devices && { devices: devices },
      tenantId: tenantId ?? "",
      createSWPolicy: false
    });
    const response = data?.createGroup;

    if (response?.success) {
      const id = response.groupDoc?.id;
      const message =
        devices && devices.length ? (
          <span>
            {t("ADD_TO_NEW_GROUP_SUCCESS", { count: devices.length })}
            &nbsp;
            {id && (
              <Link
                className="font-bold"
                to={generatePath("/manage/device-groups/details/:id", { id })}
              >
                {t("VIEW_GROUP")}
              </Link>
            )}
          </span>
        ) : (
          t("CREATE_DEVICE_GROUP_SUCCESS")
        );

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message,
        persistThroughNav: true
      });

      onSuccess && onSuccess();
      dismissModal();
    } else if (response?.error || error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: (response?.error || error?.message) ?? "err"
      });
    }
  };

  return (
    <ModalForm
      title={t("CREATE_DEVICE_GROUP")}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      name={"CreateDeviceGroupModal"}
      validationSchema={NewDeviceGroupSchema}
    >
      {({ isSubmitting, dirty, isValid }) => (
        <Form>
          <p className="small-text mt-15 mb-15">
            {t("CREATE_DEVICE_GROUP_NOTE")}
          </p>
          <div className="half-width">
            <Input name="name" label={t("GROUP_NAME")} data-testid="name" />
          </div>
          <div className="btn-container">
            <CancelButton onClick={dismissModal} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="create-group"
              disabled={!isValid || !dirty || fetching}
              labelKey="CREATE"
              submittingLabelKey="SAVING"
            />
          </div>
        </Form>
      )}
    </ModalForm>
  );
};

export default protect("UPDATE_SITES")(CreateDeviceGroupModal);
